import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, inject, Input, OnChanges, Output, signal, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { AttachmentDto } from '@models/job-detail-dto.interface';
import { AttachmentService } from '../../../services/attachment.service';
import { ServiceCompanyService } from '../../../services/service-company.service';
import { ServiceCompanyMudCheckPropertiesDto } from '@models/service-company-dto.interface';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { AttachmentType } from 'src/app/enums/attachment-type.enum';

@Component({
  selector: 'app-job-attachments',
  standalone: true,
  imports: [CommonModule, DatePipe, MatIconModule, MatButtonModule],
  templateUrl: './job-attachments.component.html',
  styleUrl: './job-attachments.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class JobAttachmentsComponent implements OnChanges {
  achmentType = AttachmentType;
  @Input() jobAttachments: AttachmentDto[] = [];
  @Input() projectAttachments: AttachmentDto[] = [];
  @Input() requestAttachments: AttachmentDto[] = [];

  @Output() changed = new EventEmitter<void>();

  private readonly attachmentService = inject(AttachmentService);
  private readonly serviceCompanyService = inject(ServiceCompanyService);

  @Input() jobId!: number;
  @Input() projectId!: number;
  //@Input() jobRequestId!: number;
  
  @ViewChild('fileInput')
  fileInput!: ElementRef<HTMLInputElement>;

  @ViewChild('mudCheckFileInput')
  mudCheckFileInput!: ElementRef<HTMLInputElement>;

  private selectedAttachmentType: AttachmentType | null = null;

  AttachmentType = AttachmentType;

  // Mud Check Properties PDFs uploaded here are automatically read and added to
  // Service Company Data - see loadMudCheckProperties()/onMudCheckPropertiesFileSelected().
  mudCheckProperties = signal<ServiceCompanyMudCheckPropertiesDto[]>([]);
  mudCheckUploadBanner = signal<{ message: string; isError: boolean } | null>(null);
  isUploadingMudCheckPdf = signal(false);
  isRecheckingMudCheckPdf = signal<number | null>(null); // Track which item is being rechecked

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['jobId'] && this.jobId) {
      this.loadMudCheckProperties();
    }
  }

  private loadMudCheckProperties(): void {
    this.serviceCompanyService.getServiceCompanyData(this.jobId).subscribe({
      next: (data) => {
        this.mudCheckProperties.set((data.mudCheckProperties || []).filter(m => !!m.pdfFileName));
      },
      error: err => console.error('Failed to load Mud Check Properties', err)
    });
  }

  protected onAddAttachment(type: AttachmentType): void {
    this.selectedAttachmentType = type;
    this.fileInput.nativeElement.click();
  }
  
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];

    this.uploadFile(file, this.selectedAttachmentType);

    // Allow selecting the same file again
    input.value = '';
  }

  
private uploadFile(
    file: File,
    attachmentType: AttachmentType | null
  ): void {
    const formData = new FormData();

    formData.append('file', file);

    switch (this.selectedAttachmentType) {
      case AttachmentType.Job:
        this.attachmentService.upload(this.jobId, this.selectedAttachmentType, file).subscribe({
          next: () => this.changed.emit(),
          error: err => console.error('Upload failed', err)
        });
        break;
      case AttachmentType.Project:
        this.attachmentService.upload(this.projectId, this.selectedAttachmentType, file).subscribe({
          next: () => this.changed.emit(),
          error: err => console.error('Upload failed', err)
        });
        break;
      // case AttachmentType.JobRequest:
      //   break;
    }
  }


  protected onOpenAttachment(att: AttachmentDto, attType: AttachmentType): void {
    this.attachmentService
      .download(att.id, attType)
      .subscribe({
        next: blob => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = att.fileName;

          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          URL.revokeObjectURL(url);
        },
        error: err => console.error('Download failed', err)
      });
  }

  protected onDeleteAttachment(att: AttachmentDto, attType: AttachmentType): void {
    this.attachmentService
      .delete(att.id, attType)
      .subscribe({
        next: () => {
          this.changed.emit();
        },
        error: err => console.error('Delete failed', err)
      });
  }

  // ========== Mud Check Properties (Service Company Data) ==========
  protected onAddMudCheckPropertiesPdf(): void {
    this.mudCheckFileInput.nativeElement.click();
  }

  onMudCheckPropertiesFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files || files.length === 0) return;

    const file = files[0];

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      this.mudCheckUploadBanner.set({ message: 'Only PDF files are supported.', isError: true });
      input.value = '';
      return;
    }

    const maxSizeMB = 10;
    if (file.size > maxSizeMB * 1024 * 1024) {
      this.mudCheckUploadBanner.set({ message: `File size exceeds ${maxSizeMB}MB limit.`, isError: true });
      input.value = '';
      return;
    }

    this.isUploadingMudCheckPdf.set(true);
    this.mudCheckUploadBanner.set(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const fileContent = e.target?.result as ArrayBuffer;

      // mudCheckPropertiesId 0 -> creates a brand new Mud Check Properties record for this job
      this.serviceCompanyService.uploadTestDataPdf(this.jobId, 0, file.name, fileContent).subscribe({
        next: (result) => {
          this.mudCheckUploadBanner.set({
            message: `"${file.name}" uploaded. ${result.message} Open the Service Company Data tab to review and save.`,
            isError: false
          });
          this.loadMudCheckProperties();
          this.serviceCompanyService.notifyMudCheckPropertiesChanged(this.jobId);
          this.isUploadingMudCheckPdf.set(false);
        },
        error: (err) => {
          this.mudCheckUploadBanner.set({ message: err?.error?.error || 'Failed to upload PDF.', isError: true });
          this.isUploadingMudCheckPdf.set(false);
        }
      });
    };
    reader.readAsArrayBuffer(file);

    input.value = '';
  }

  protected onOpenMudCheckPropertiesPdf(item: ServiceCompanyMudCheckPropertiesDto): void {
    this.serviceCompanyService.downloadMudCheckPropertiesPdf(this.jobId, item.id).subscribe({
      next: blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = item.pdfFileName || 'mud-check-properties.pdf';

        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        URL.revokeObjectURL(url);
      },
      error: err => console.error('Download failed', err)
    });
  }

  protected onDeleteMudCheckPropertiesPdf(item: ServiceCompanyMudCheckPropertiesDto): void {
    this.serviceCompanyService.deleteMudCheckPropertiesRecord(this.jobId, item.id).subscribe({
      next: () => {
        this.loadMudCheckProperties();
        this.serviceCompanyService.notifyMudCheckPropertiesChanged(this.jobId);
      },
      error: err => console.error('Delete failed', err)
    });
  }

  protected onRecheckMudCheckPropertiesPdf(item: ServiceCompanyMudCheckPropertiesDto): void {
    this.isRecheckingMudCheckPdf.set(item.id);
    this.serviceCompanyService.rereadMudCheckPropertiesPdf(this.jobId, item.id).subscribe({
      next: () => {
        this.mudCheckUploadBanner.set({
          message: `"${item.pdfFileName}" rechecked. Open the Service Company Data tab to review updates.`,
          isError: false
        });
        this.loadMudCheckProperties();
        this.serviceCompanyService.notifyMudCheckPropertiesChanged(this.jobId);
        this.isRecheckingMudCheckPdf.set(null);
      },
      error: (err) => {
        this.mudCheckUploadBanner.set({ message: err?.error?.error || 'Failed to recheck PDF.', isError: true });
        this.isRecheckingMudCheckPdf.set(null);
      }
    });
  }

  protected dismissMudCheckUploadBanner(): void {
    this.mudCheckUploadBanner.set(null);
  }
}